import { useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { useFocusTrap } from '@/hooks/useFocusTrap'
import { DrawerNavContent } from './DrawerNavContent'
import styles from './AppDrawer.module.css'

type Props = {
  open: boolean
  onClose: () => void
  /** Element to return focus to when the drawer closes (the
   * hamburger button in AppTopBar). */
  returnFocusRef?: React.RefObject<HTMLElement | null>
  /** The head's own link. Defaults to the platform wordmark. The CLUB STOREFRONT
   *  passes the club's name and page instead: a club pays for a page that does
   *  not say "The Racket Center" at its visitors, and that rule does not stop at
   *  the header — the drawer opens over the same page. */
  brand?: { label: string; to: string }
  /** Draw the app list alone — no COMPANY group, no MANAGE / ACCOUNT tails, no
   *  CTA. See DrawerNavContent's own note; set by the club storefront. */
  navOnly?: boolean
}

const PLATFORM_BRAND = { label: 'The Racket Center', to: '/' }

/**
 * Slide-in navigation drawer, and the ONLY menu on two surfaces that have no
 * sidebar: the workspace below 1024px, and the club storefront at every width.
 * Both draw the same APP_NAV_ITEMS the desktop sidebar and the public sheet draw
 * (owner, 2026-09-06 / 2026-09-07) so the one menu reads the same wherever it
 * opens — the drawer is handed no list of its own to pass on.
 * Closes on backdrop click, close button, ESC key, and route changes (handled by
 * the host — AppShell and StorefrontHeader each watch useLocation).
 *
 * The storefront differs only in what it may SAY, never in how it behaves: same
 * dialog, same focus trap, same rows — a different head, and the marketing +
 * account groups dropped. Both are props, so there is one drawer, not two.
 *
 * See UX brief docs/ux/2026-05-16-workspace-luxury-redesign.md §4.6.
 */
export function AppDrawer({
  open,
  onClose,
  returnFocusRef,
  brand = PLATFORM_BRAND,
  navOnly = false,
}: Props) {
  const closeButtonRef = useRef<HTMLButtonElement | null>(null)
  const panelRef = useRef<HTMLElement | null>(null)

  // Trap Tab focus within the open drawer; focus-in (close button) and
  // focus-restore (hamburger) are handled by the effect below.
  useFocusTrap(open, panelRef)

  // ESC closes the drawer (focus return is handled in the cleanup
  // below when `open` flips back to false).
  useEffect(() => {
    if (!open) return undefined
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  // When the drawer opens, focus moves to the close button so a
  // keyboard user immediately lands on a known control; when it
  // closes, focus returns to the hamburger button so the keyboard
  // user's place in the page is preserved.
  useEffect(() => {
    if (open) {
      closeButtonRef.current?.focus()
    } else {
      returnFocusRef?.current?.focus()
    }
  }, [open, returnFocusRef])

  return (
    <div
      className={`${styles.root} ${open ? styles.rootOpen : ''}`.trim()}
      aria-hidden={!open}
    >
      <div
        className={styles.backdrop}
        onClick={onClose}
        role="presentation"
      />
      <aside
        ref={panelRef}
        className={styles.panel}
        role="dialog"
        aria-modal="true"
        aria-label="Primary navigation"
      >
        <div className={styles.head}>
          <Link to={brand.to} className={styles.brand} onClick={onClose}>
            {brand.label}
          </Link>
          <button
            type="button"
            ref={closeButtonRef}
            className={styles.closeButton}
            aria-label="Close navigation"
            onClick={onClose}
          >
            <CloseIcon />
          </button>
        </div>
        <DrawerNavContent onNavigate={onClose} navOnly={navOnly} />
      </aside>
    </div>
  )
}

function CloseIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="20"
      height="20"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      aria-hidden="true"
    >
      <path d="M6 6l12 12" />
      <path d="M18 6L6 18" />
    </svg>
  )
}
